<template>
  <div role="dialog" class="top-14 right-5 fadein absolute flex justify-center items-center">
    <div class="popup-wrapper justify-center max-w-md flex flex-col rounded-xl note-border bg-blue-middle">
      <div class="flex flex-row justify-end">
        <div tabindex="0" ref="firstFocus" class="h-6 w-6 mx-2 mt-3 p-1.5 bg-black rounded-full flex justify-center items-center cursor-pointer">
          <img class="w-5 h-5 invert rotate-45" :src="Close" :alt="t('aria-close')" @click.prevent="close()" />
        </div>
      </div>
      <div class="flex flex-col">
        <div v-if="props.wall.details.pin" class="text-xl font-bold">{{ wallUrl() }}</div>
        <p v-if="props.wall.details.pin" class="m-3 text-7xl text-white font-black">{{ props.wall.details.pin }}</p>
        <div v-if="props.wall.status.permaLink" class="w-full flex flex-col justify-center items-center mt-3">
          <qrcode-vue :value="assembledPermaLink" :size="240" />
          <button
            v-if="props.wall.status.permaLink"
            class="rounded-3xl border border-white cursor-pointer p-3 px-5 mt-2 text-sm font-bold bg-blue-middle"
            @click="copyToClipBoard(assembledPermaLink)"
          >
            {{ t('copyLink') }}
          </button>
          <!-- Hidden placeholder for the permalink text so that selenium can grab it directly from frontend -->
          <div id="permaLinkText" class="hidden" v-text="assembledPermaLink"></div>
        </div>
      </div>
      <div class="relative flex flex-col mt-4">
        <div
          v-if="(feideSharing.length > 0 || canvasSharing.length > 0) && state.selectedSharingIndex === 0"
          class="pointer-events-none flex flex-col justify-between rounded-xl"
        >
          <div class="rounded-xl p-7 flex flex-col">
            <!--p class="text-xl font-bold">{{ wallUrl() }}</p-->
            <p class="mt-4 text-xl text-white font-black">{{ props.wall.details.title }}</p>
            <div class="flex flex-col justify-between items-center mt-5">
              <p v-if="feideSharing.length > 0">{{ t('sharedinfeide') }}</p>
              <p v-if="canvasSharing.length > 0">{{ t('sharedincanvas') }}</p>
              <div class="flex rounded-3xl content-center items-center">
                <img :src="UserIcon" class="invert w-3 mr-2" :alt="t('aria-user-icon')" />
                <p v-if="props.wall.details.contributors" class="font-medium">{{ props.wall.details.contributors.length - 1 }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, PropType, reactive, ref } from 'vue'
  import QrcodeVue from 'qrcode.vue'
  import { useI18n } from 'vue-i18n'

  import { DialogMessageType, Sharing, Wall } from '@/common/classes'
  import { clientBaseUrl, GroupType, PermissionLevel } from '@/constants'
  import useCommonStore from '@/composables/common'
  import useDialogStore from '@/composables/dialog'

  import Arrow from '@/assets/icons/fontawesome/angle-left-solid.svg'
  import UserIcon from '@/assets/icons/fontawesome/user-solid.svg'
  import Close from '@/assets/icons/fontawesome/plus-solid.svg'

  const messages = {
    no: {
      copyLink: 'Kopier lenke',
    },
    en: {
      copyLink: 'Copy link',
    },
  }

  const { t } = useI18n({ messages })
  const common = useCommonStore()
  const dialogStore = useDialogStore()

  const emit = defineEmits<{
    (e: 'closePopup'): void
  }>()

  const props = defineProps({
    wall: { type: Object as PropType<Wall>, required: true },
  })

  const state = reactive({
    selectedSharingIndex: 0,
  })

  const firstFocus = ref()

  onMounted(() => {
    nextTick(() => firstFocus.value?.focus())
  })
  const assembledPermaLink = computed(() => {
    const port = import.meta.env.VITE_SERVER_HOST.includes('localhost') ? `:${import.meta.env.VITE_CLIENT_PORT}` : ''
    return `${import.meta.env.VITE_SERVER_HOST}${port}/login?link=${props.wall.details.permaLink}`
  })

  function copyToClipBoard(text: string) {
    common.actions.copyTextToClipboard(text)
    dialogStore.actions.pushMessage('', DialogMessageType.Information, t('copiedtoclipboard'))
  }

  const feideSharing = computed(() => {
    return props.wall.users.shares.filter((s: Sharing) => s.groupType === GroupType.DATAPORTEN && s.permissionLevel !== PermissionLevel.Copy)
  })

  const canvasSharing = computed(() => {
    return props.wall.users.shares.filter((s: Sharing) => s.groupType === GroupType.CANVAS && s.permissionLevel !== PermissionLevel.Copy)
  })

  const thirdPartySharing = computed(() => {
    return [...feideSharing.value, ...canvasSharing.value]
  })

  function navigationStyles(target: number) {
    return (target === -1 && state.selectedSharingIndex > 0) || (target === 1 && state.selectedSharingIndex < thirdPartySharing.value.length - 1)
      ? ''
      : 'opacity-50'
  }

  function navigate(value: number) {
    if (state.selectedSharingIndex + value < thirdPartySharing.value.length && state.selectedSharingIndex + value >= 0)
      state.selectedSharingIndex += value
  }

  function wallUrl() {
    return `${clientBaseUrl}` //${route.fullPath}
  }

  function close() {
    emit('closePopup')
  }
</script>

<style scoped>
  div:focus {
    outline: 2px solid #fa35e2;
    outline-offset: 2px;
  }
</style>
